import { api } from './api';

export class AudioLevelMonitor {
  private audioCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private animFrameId: number | null = null;
  private onSpeakingChange: (speaking: boolean) => void;
  private isSpeaking = false;

  constructor(onSpeakingChange: (speaking: boolean) => void) {
    this.onSpeakingChange = onSpeakingChange;
  }

  start(stream: MediaStream): void {
    try {
      const audioTracks = stream.getAudioTracks();
      if (!audioTracks.length) return;

      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      this.audioCtx = new AudioCtx();
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 256;
      this.source = this.audioCtx.createMediaStreamSource(stream);
      this.source.connect(this.analyser);

      const bufferLength = this.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const checkAudio = () => {
        if (!this.analyser) return;
        this.analyser.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const avg = sum / bufferLength;
        const speaking = avg > 18;

        if (speaking !== this.isSpeaking) {
          this.isSpeaking = speaking;
          this.onSpeakingChange(speaking);
        }

        this.animFrameId = requestAnimationFrame(checkAudio);
      };

      checkAudio();
    } catch (e) {
      console.warn('AudioLevelMonitor failed to start:', e);
    }
  }

  stop(): void {
    if (this.animFrameId) cancelAnimationFrame(this.animFrameId);
    if (this.source) {
      try { this.source.disconnect(); } catch {}
    }
    if (this.audioCtx) {
      try { this.audioCtx.close(); } catch {}
    }
    this.audioCtx = null;
    this.analyser = null;
    this.source = null;
    this.animFrameId = null;
    this.isSpeaking = false;
  }
}

interface PeerConnectionWrapper {
  pc: RTCPeerConnection;
  remoteStream: MediaStream;
  candidateQueue: RTCIceCandidateInit[];
  makingOffer: boolean;
}

export class WebRTCManager {
  private publicId: string;
  private myParticipantId?: string;
  private iceServers: RTCIceServer[] = [];
  private localStream: MediaStream | null = null;
  private screenStream: MediaStream | null = null;
  private peers = new Map<string, PeerConnectionWrapper>();
  private onRemoteTrack: (participantId: string, stream: MediaStream) => void;
  private sendSignalFn?: (targetParticipantId: string, signalData: any) => void;
  private onConnectionStateChange?: (state: RTCPeerConnectionState) => void;

  // Cloudflare Calls SFU handles
  private publishPc: RTCPeerConnection | null = null;
  private subscribePc: RTCPeerConnection | null = null;
  private publishSessionId: string | null = null;
  private subscribeSessionId: string | null = null;

  constructor(
    publicId: string,
    onRemoteTrack: (participantId: string, stream: MediaStream) => void,
    myParticipantId?: string,
    onConnectionStateChange?: (state: RTCPeerConnectionState) => void
  ) {
    this.publicId = publicId;
    this.myParticipantId = myParticipantId;
    this.onRemoteTrack = onRemoteTrack;
    this.onConnectionStateChange = onConnectionStateChange;
  }

  setMyParticipantId(id: string): void {
    this.myParticipantId = id;
  }

  setSendSignal(fn: (targetParticipantId: string, signalData: any) => void): void {
    this.sendSignalFn = fn;
  }

  async init(localStream?: MediaStream): Promise<void> {
    this.localStream = localStream || null;
    try {
      const iceData = await api.getIceServers(this.publicId);
      this.iceServers = iceData.iceServers && iceData.iceServers.length > 0
        ? iceData.iceServers
        : [{ urls: ['stun:stun.cloudflare.com:3478'] }];
    } catch {
      this.iceServers = [{ urls: ['stun:stun.cloudflare.com:3478'] }];
    }
  }

  // Connects to a specific remote peer via Cloudflare DO signaling
  async connectToPeer(remoteParticipantId: string, isInitiator = false): Promise<void> {
    if (!remoteParticipantId || remoteParticipantId === this.myParticipantId) return;
    if (this.peers.has(remoteParticipantId)) return;

    const wrapper = this.createPeerWrapper(remoteParticipantId);
    this.peers.set(remoteParticipantId, wrapper);

    if (isInitiator) {
      try {
        wrapper.makingOffer = true;
        const offer = await wrapper.pc.createOffer();
        await wrapper.pc.setLocalDescription(offer);
        this.sendSignalFn?.(remoteParticipantId, {
          type: 'offer',
          sdp: offer.sdp,
        });
      } catch (err) {
        console.error(`Failed to create offer for peer ${remoteParticipantId}:`, err);
      } finally {
        wrapper.makingOffer = false;
      }
    }
  }

  private createPeerWrapper(remoteParticipantId: string): PeerConnectionWrapper {
    const pc = new RTCPeerConnection({ iceServers: this.iceServers });
    const remoteStream = new MediaStream();

    const wrapper: PeerConnectionWrapper = {
      pc,
      remoteStream,
      candidateQueue: [],
      makingOffer: false,
    };

    // Attach local audio/video tracks to peer connection
    const currentStream = this.screenStream || this.localStream;
    if (currentStream) {
      for (const track of currentStream.getTracks()) {
        pc.addTrack(track, currentStream);
      }
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendSignalFn?.(remoteParticipantId, {
          type: 'candidate',
          candidate: event.candidate.toJSON(),
        });
      }
    };

    pc.ontrack = (event) => {
      // Add track to peer's remote stream
      if (!remoteStream.getTracks().some((t) => t.id === event.track.id)) {
        remoteStream.addTrack(event.track);
      }
      this.onRemoteTrack(remoteParticipantId, remoteStream);

      event.track.onended = () => {
        remoteStream.removeTrack(event.track);
        this.onRemoteTrack(remoteParticipantId, remoteStream);
      };
    };

    pc.onconnectionstatechange = () => {
      this.onConnectionStateChange?.(pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        console.log(`Connection state with ${remoteParticipantId}: ${pc.connectionState}`);
      }
    };

    return wrapper;
  }

  // Handle incoming signaling message from another peer
  async handleSignal(fromParticipantId: string, signalData: any): Promise<void> {
    if (!fromParticipantId || !signalData) return;

    let wrapper = this.peers.get(fromParticipantId);
    if (!wrapper) {
      wrapper = this.createPeerWrapper(fromParticipantId);
      this.peers.set(fromParticipantId, wrapper);
    }

    const { pc } = wrapper;

    try {
      if (signalData.type === 'offer' && signalData.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: signalData.sdp }));

        // Drain queued ICE candidates
        while (wrapper.candidateQueue.length > 0) {
          const cand = wrapper.candidateQueue.shift();
          if (cand) {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          }
        }

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        this.sendSignalFn?.(fromParticipantId, {
          type: 'answer',
          sdp: answer.sdp,
        });
      } else if (signalData.type === 'answer' && signalData.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: signalData.sdp }));

        // Drain queued ICE candidates
        while (wrapper.candidateQueue.length > 0) {
          const cand = wrapper.candidateQueue.shift();
          if (cand) {
            await pc.addIceCandidate(new RTCIceCandidate(cand));
          }
        }
      } else if (signalData.type === 'candidate' && signalData.candidate) {
        if (pc.remoteDescription && pc.remoteDescription.type) {
          await pc.addIceCandidate(new RTCIceCandidate(signalData.candidate));
        } else {
          wrapper.candidateQueue.push(signalData.candidate);
        }
      }
    } catch (err) {
      console.warn(`Error handling signal from ${fromParticipantId}:`, err);
    }
  }

  removePeer(remoteParticipantId: string): void {
    const wrapper = this.peers.get(remoteParticipantId);
    if (wrapper) {
      try { wrapper.pc.close(); } catch {}
      this.peers.delete(remoteParticipantId);
    }
  }

  // Updates local stream and replaces tracks on all active peer connections
  updateLocalStream(newStream: MediaStream): void {
    this.localStream = newStream;
    if (this.screenStream) return; // Screen share takes precedence for video

    for (const [_, wrapper] of this.peers) {
      const senders = wrapper.pc.getSenders();
      for (const track of newStream.getTracks()) {
        const sender = senders.find((s) => s.track && s.track.kind === track.kind);
        if (sender) {
          sender.replaceTrack(track).catch(() => {});
        } else {
          try { wrapper.pc.addTrack(track, newStream); } catch {}
        }
      }
    }
  }

  // Screen Sharing
  async shareScreen(): Promise<MediaStream | null> {
    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true,
      });

      const screenVideoTrack = this.screenStream.getVideoTracks()[0];
      if (screenVideoTrack) {
        // Seamlessly replace video track on all peer connections
        for (const [_, wrapper] of this.peers) {
          const videoSender = wrapper.pc.getSenders().find((s) => s.track && s.track.kind === 'video');
          if (videoSender) {
            await videoSender.replaceTrack(screenVideoTrack);
          } else {
            wrapper.pc.addTrack(screenVideoTrack, this.screenStream);
          }
        }

        screenVideoTrack.onended = () => {
          this.stopScreenShare();
        };
      }

      return this.screenStream;
    } catch (e) {
      console.error('Screen sharing canceled or failed:', e);
      return null;
    }
  }

  async stopScreenShare(): Promise<void> {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach((t) => t.stop());
      this.screenStream = null;
    }

    // Restore camera video track on all peers
    const cameraTrack = this.localStream?.getVideoTracks()[0] || null;
    for (const [_, wrapper] of this.peers) {
      const videoSender = wrapper.pc.getSenders().find((s) => s.track && s.track.kind === 'video');
      if (videoSender && cameraTrack) {
        await videoSender.replaceTrack(cameraTrack);
      }
    }
  }

  // Cloudflare Calls SFU Auto-Publish
  async connectSfu(): Promise<void> {
    try {
      const sessions = await api.createSfuSessions(this.publicId);
      this.publishSessionId = sessions.publishSessionId;
      this.subscribeSessionId = sessions.subscribeSessionId;

      if (!this.publishSessionId || this.publishSessionId.startsWith('sfu_')) {
        // SFU is in local fallback mode, peer mesh handles delivery
        return;
      }

      this.publishPc = new RTCPeerConnection({ iceServers: this.iceServers });

      if (this.localStream) {
        for (const track of this.localStream.getTracks()) {
          this.publishPc.addTrack(track, this.localStream);
        }

        const offer = await this.publishPc.createOffer();
        await this.publishPc.setLocalDescription(offer);

        const tracks = this.publishPc.getTransceivers().map((t) => ({
          location: 'local',
          mid: t.mid,
          trackName: t.sender.track?.kind === 'audio' ? 'microphone' : 'camera',
        }));

        const result = await api.publishSfu(
          this.publicId,
          this.publishSessionId,
          this.publishPc.localDescription!,
          tracks
        );

        if (result.sessionDescription) {
          await this.publishPc.setRemoteDescription(result.sessionDescription);
        }
      }
    } catch (e) {
      console.warn('Cloudflare Calls SFU auto-connect deferred (using Cloudflare TURN peer mesh):', e);
    }
  }

  close(): void {
    this.stopScreenShare();
    for (const [_, wrapper] of this.peers) {
      try { wrapper.pc.close(); } catch {}
    }
    this.peers.clear();

    if (this.publishPc) {
      try { this.publishPc.close(); } catch {}
      this.publishPc = null;
    }
    if (this.subscribePc) {
      try { this.subscribePc.close(); } catch {}
      this.subscribePc = null;
    }
  }
}
